import { Injectable } from '@angular/core';
import { AclService } from '@y42/acl';
import {
  AclAssetModulePermission,
  AclAssetModuleResourceType,
  AclPermission,
  AclPermissionDetail,
  AclPermissionsRecord,
  AclStrategy,
  AclResource,
  AclRole,
  AclRoleAssignment,
  AclSelectRolesContext,
  AclSubject,
  assetModuleValues,
} from '@y42/acl/models';
import { AclQuery } from '@y42/acl/state';
import { ErrorHandlerService } from '@y42/error-handling';
import { OrgSpaceQuery } from '@y42/org-space/state';
import { isNullish } from '@y42/util/nullish';
import { Observable, of } from 'rxjs';

/**
 * The resource id of an asset module is the resource type.
 */
@Injectable({ providedIn: 'root' })
export class AssetModuleAclService {
  constructor(
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly aclQuery: AclQuery,
    private readonly aclService: AclService,
    private readonly orgSpaceQuery: OrgSpaceQuery,
  ) {}

  enumerateAssetModule({
    resourceType,
    subject,
    spaceId,
    permissions,
  }: {
    subject?: AclSubject;
    resourceType: AclAssetModuleResourceType;
    spaceId?: string;
    permissions: AclPermission[];
  }): Observable<AclResource[]> {
    spaceId = spaceId ?? this.orgSpaceQuery.getActive()?.id;

    return this.aclService.enumerateResources({
      resourceType,
      subject,
      spaceId,
      permissions,
    });
  }

  getAssetModulePermission({
    resourceType,
    permission,
    spaceId,
  }: {
    resourceType: AclAssetModuleResourceType;
    permission: AclAssetModulePermission;
    spaceId?: string;
  }): AclPermissionDetail | null {
    spaceId = spaceId ?? this.orgSpaceQuery.getActive()?.id;

    if (!spaceId) {
      this.errorHandlerService.error('[OrgSpaceAclService.getAssetModulePermission]: nullish spaceId');
      return { allowed: false };
    }

    return this.aclQuery.getPermission({
      resourceType,
      resourceId: spaceId,
      permission,
    });
  }

  checkAssetModulePermission({
    resourceType,
    permission,
    spaceId,
  }: {
    resourceType: AclAssetModuleResourceType;
    permission: AclAssetModulePermission;
    spaceId?: string;
  }): Observable<AclPermissionDetail> {
    spaceId = spaceId ?? this.orgSpaceQuery.getActive()?.id;

    if (isNullish(spaceId)) {
      this.errorHandlerService.error(`[AssetModuleAclService.checkAssetModulePermission]: nullish spaceId`);
      return of({ allowed: false });
    }

    return this.aclService.checkPermission({
      resourceType,
      resourceId: resourceType,
      permission,
      spaceId,
    });
  }

  checkAssetModulePermissions({
    resourceType,
    permissions,
    spaceId,
  }: {
    resourceType: AclAssetModuleResourceType;
    permissions: AclAssetModulePermission[];
    spaceId?: string;
  }): Observable<AclPermissionsRecord> {
    spaceId = spaceId ?? this.orgSpaceQuery.getActive()?.id;

    if (isNullish(spaceId)) {
      this.errorHandlerService.error(`[AssetModuleAclService.checkAssetModulePermissions]: nullish spaceId`);
      return of({} as AclPermissionsRecord);
    }

    return this.aclService.checkPermissions({
      resourceType,
      resourceId: resourceType,
      permissions,
      spaceId,
    });
  }

  getAssetModuleRoles({ resourceType }: { resourceType: AclAssetModuleResourceType }): AclRole[] {
    return this.aclQuery.getRoles({ resourceType });
  }

  convertAssetModuleRoleToGrantPermission(role: AclRole): AclAssetModulePermission {
    return this.aclService.convertRoleToGrantPermission(role) as AclAssetModulePermission;
  }

  getAssetModuleRoleTooltip({
    role,
    resourceType,
  }: {
    role: AclRole;
    resourceType: AclAssetModuleResourceType;
  }): string {
    return this.aclQuery.getRoleTooltip({ role, resourceType });
  }

  filterAssetModuleRolesBasedOnGrantedPermissions({
    roles,
    resourceType,
    spaceId,
    strategy,
  }: {
    roles: AclRole[];
    resourceType: AclAssetModuleResourceType;
    spaceId?: string;
    strategy?: AclStrategy;
  }): Observable<AclRole[]> {
    spaceId = spaceId ?? this.orgSpaceQuery.getActive()?.id;

    if (isNullish(spaceId)) {
      this.errorHandlerService.error(
        `[AssetModuleAclService.filterAssetModuleRolesBasedOnGrantedPermissions]: nullish spaceId`,
      );
      return of([]);
    }

    return this.aclService.filterRolesBasedOnGrantedPermissions({
      roles,
      resourceType,
      resourceId: resourceType,
      spaceId,
      strategy,
    });
  }

  setAssetModuleRole({
    resourceType,
    spaceId,
    role,
    subject,
  }: {
    resourceType: AclAssetModuleResourceType;
    spaceId?: string;
    role: AclRole;
    subject: AclSubject;
  }): Observable<boolean> {
    spaceId = spaceId ?? this.orgSpaceQuery.getActive()?.id;

    if (isNullish(spaceId)) {
      this.errorHandlerService.error('[AssetModuleAclService.setAssetModuleRole]: nullish spaceId');
      return of(false);
    }

    return this.aclService
      .setRole({
        resourceType,
        resourceId: resourceType,
        spaceId,
        subject,
        role,
      })
      .pipe(
        this.errorHandlerService.catchError(
          '[AssetModuleAclService.setAssetModuleRole]: could not set role for resource module',
          () => of(false),
          {
            toast: `Could not set the role "${role}" for resource module "${resourceType}"`,
            context: { role, resourceType },
          },
        ),
      );
  }

  revokeAssetModuleRole({
    subject,
    resourceType,
    spaceId,
  }: {
    resourceType: AclAssetModuleResourceType;
    spaceId?: string;
    subject: AclSubject;
  }): Observable<boolean> {
    spaceId = spaceId ?? this.orgSpaceQuery.getActive()?.id;

    if (isNullish(spaceId)) {
      this.errorHandlerService.error('[AssetModuleAclService.revokeAssetModuleRole]: nullish spaceId');
      return of(false);
    }

    return this.aclService
      .revokeRole({
        resourceType,
        resourceId: resourceType,
        spaceId,
        subject,
      })
      .pipe(
        this.errorHandlerService.catchError(
          '[AssetModuleAclService.revokeAssetModuleRole]: could not revoke role for resource module',
          () => of(false),
          {
            toast: `Could not revoke the role for resource module "${resourceType}"`,
            context: { resourceType },
          },
        ),
      );
  }

  getAssetModuleRoleAssignments({
    resourceType,
    spaceId,
    subject,
    strategy,
  }: {
    resourceType: AclAssetModuleResourceType;
    spaceId?: string;
    subject?: AclSubject;
    strategy?: AclStrategy;
  }): Observable<AclRoleAssignment[]> {
    spaceId = spaceId ?? this.orgSpaceQuery.getActive()?.id;

    if (isNullish(spaceId)) {
      this.errorHandlerService.error('[AssetModuleAclService.getAssetModuleRoleAssignments]: nullish spaceId');
      return of([]);
    }

    return this.aclService
      .getRoleAssignments({
        resourceType,
        resourceId: resourceType,
        spaceId,
        subject,
        strategy,
      })
      .pipe(
        this.errorHandlerService.catchError(
          `[AssetModuleAclService.getAssetModuleRoleAssignments]: could not fetch roles`,
          () => of([]),
          {
            toast: `Could not fetch roles for resource module "${resourceType}"`,
            context: { resourceType },
          },
        ),
      );
  }

  selectAssetModuleRoleAssignments({
    resourceType,
  }: {
    resourceType: AclAssetModuleResourceType;
  }): Observable<AclRoleAssignment[]> {
    return this.aclQuery.selectRoleAssignments({
      resourceType,
      resourceId: resourceType,
    });
  }

  getAssetModuleRolesContextForRoleSelect({
    resourceType,
    spaceId,
    subject,
    strategy,
  }: {
    resourceType: AclAssetModuleResourceType;
    spaceId: string;
    subject?: AclSubject;
    strategy?: AclStrategy;
  }): Observable<AclSelectRolesContext> {
    return this.aclService.getRolesContextForRoleSelect({
      resourceId: resourceType,
      resourceType,
      spaceId,
      subject,
      strategy,
    });
  }

  resetAllAssetModuleRoleAssignments(): void {
    this.aclService.resetAllRoleAssignments({
      resourceTypes: assetModuleValues,
    });
  }
}
